/**
 * Colour decisions for charts: which palette slot a series gets, and which ink a label
 * sitting on a coloured mark needs.
 *
 * The palette itself is `CHART_COLORS` (Okabe–Ito). Series take slots in a fixed order
 * from a chosen starting colour, so a series keeps its colour when others are added; the
 * charts cap themselves at eight series rather than cycling back to slot one, which would
 * give two series the same colour.
 */

import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/** Palette slot names, in `CHART_COLORS` order. Widgets map these to their own labels. */
export const CHART_COLOR_IDS = [
  "blue",
  "orange",
  "green",
  "vermillion",
  "skyBlue",
  "purple",
  "yellow",
  "black",
] as const;

export type ChartColorId = (typeof CHART_COLOR_IDS)[number];

/** The most series a chart colours distinctly. */
export const MAX_SERIES = CHART_COLORS.length;

/** The colour of series `index` when the first series uses slot `start`. */
export function paletteColor(start: number, index: number): string {
  const slot =
    (((start + index) % CHART_COLORS.length) + CHART_COLORS.length) %
    CHART_COLORS.length;
  return CHART_COLORS[slot];
}

export function colorIndex(id: ChartColorId): number {
  return Math.max(0, CHART_COLOR_IDS.indexOf(id));
}

function channel(hex: string, offset: number): number {
  const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
  return value <= 0.039_28 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a `#rrggbb` colour. */
export function relativeLuminance(hex: string): number {
  return (
    0.2126 * channel(hex, 1) +
    0.7152 * channel(hex, 3) +
    0.0722 * channel(hex, 5)
  );
}

/** WCAG contrast ratio between two `#rrggbb` colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Dark ink or white, whichever reads better on `fill` — for labels inside a mark. */
export function textColorOn(fill: string): string {
  return contrastRatio(fill, CHART_INK.text) >= contrastRatio(fill, "#ffffff")
    ? CHART_INK.text
    : "#ffffff";
}
